import React, { useContext, useEffect } from "react";

import { AppBar, Divider, Stack, Toolbar } from "@mui/material";
import ImageIcon from "@mui/icons-material/Image";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";

import { ChimeMessageContext, MeetingInfoContext } from "../../../context";
import { useGetMeetingUrls } from "../../../store";
import { ResourceType } from "../../../types";

import { ClearCallLogo, ClearCallTitle } from "../../clear-call-logo";
import {
  AttendeeSelect,
  CallQuality,
  ImageViewerModal,
  ModelViewerModal,
  ViewImagesButton,
  ViewModelsButton,
} from "./components";

interface MeetingToolbarProps {
  children?: React.ReactNode;
}

const MeetingToolbar: React.FC<MeetingToolbarProps> = ({ children }) => {
  const { connected, userId, meetingId, joinCode } =
    useContext(MeetingInfoContext);

  const [lastModelCount, setLastModelCount] = React.useState(-1);
  const [lastPhotoCount, setLastPhotoCount] = React.useState(-1);

  // Image modal state
  const [imageModalOpen, setImageModalOpen] = React.useState(false);
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
  const [seenImages, setSeenImages] = React.useState<number[]>([]);

  // Model modal state
  const [modelModalOpen, setModelModalOpen] = React.useState(false);
  const [currentModelIndex, setCurrentModelIndex] = React.useState(0);
  const [seenModels, setSeenModels] = React.useState<number[]>([]);

  const { data: urlSet = null } = useGetMeetingUrls(meetingId, userId, joinCode);

  const queryClient = useQueryClient();
  const { subscribeToChannel } = useContext(ChimeMessageContext);
  const [listeningForUpload, setListeningForUpload] = React.useState(false);

  // Subscribe to upload-image channel to refetch when headset uploads a photo
  useEffect(() => {
    if (listeningForUpload || !connected) {
      return;
    }

    subscribeToChannel(
      "upload-image",
      "meeting-control-bar",
      (message) => {
        if (message.payload === "complete") {
          queryClient.invalidateQueries({ queryKey: [ResourceType.Meeting, meetingId] });
        }
      }
    );

    setListeningForUpload(true);
  }, [connected, listeningForUpload, meetingId, queryClient, subscribeToChannel]);

  // Image modal helpers
  const getFirstUnseenImageIndex = () => {
    const downloadURLs = urlSet?.photoUrls || [];
    let firstUnseenIndex = currentImageIndex;

    for (let i = 0; i < downloadURLs.length; i++) {
      if (!seenImages.includes(i)) {
        firstUnseenIndex = i;
        break;
      }
    }

    return firstUnseenIndex;
  };

  const selectCurrentImageIndex = (index: number) => {
    if (!seenImages.includes(index)) {
      setSeenImages([...seenImages, index]);
    }
    setCurrentImageIndex(index);
  };

  const openImageModal = () => {
    setImageModalOpen(true);
    const firstUnseenIndex = getFirstUnseenImageIndex();
    setCurrentImageIndex(firstUnseenIndex);
    if (!seenImages.includes(firstUnseenIndex)) {
      setSeenImages([...seenImages, firstUnseenIndex]);
    }
  };

  const closeImageModal = () => {
    setImageModalOpen(false);
  };

  // Model modal helpers
  const getFirstUnseenModelIndex = () => {
    const imageSources = urlSet?.modelUrls || [];
    let firstUnseenIndex = currentModelIndex;

    for (let i = 0; i < imageSources.length; i++) {
      if (!seenModels.includes(i)) {
        firstUnseenIndex = i;
        break;
      }
    }

    return firstUnseenIndex;
  };

  const selectCurrentModelIndex = (index: number) => {
    if (!seenModels.includes(index)) {
      setSeenModels([...seenModels, index]);
    }
    setCurrentModelIndex(index);
  };

  const openModelModal = () => {
    setModelModalOpen(true);
    const firstUnseenIndex = getFirstUnseenModelIndex();
    setCurrentModelIndex(firstUnseenIndex);
    if (!seenModels.includes(firstUnseenIndex)) {
      setSeenModels([...seenModels, firstUnseenIndex]);
    }
  };

  const closeModelModal = () => {
    setModelModalOpen(false);
  };

  useEffect(() => {
    if (!urlSet) {
      return;
    }

    if (!urlSet.photoUrls || !urlSet.modelUrls) {
      return
    }

    if (lastModelCount === -1 && lastPhotoCount === -1) {
      setLastModelCount(urlSet.photoUrls.length);
      setLastPhotoCount(urlSet.modelUrls.length);
      return;
    }

    const newModelAdded = urlSet.modelUrls.length > lastModelCount;
    const newPhotoAdded = urlSet.photoUrls.length > lastPhotoCount;

    if (!newModelAdded && !newPhotoAdded) {
      return;
    }

    if (newPhotoAdded) {
      toast("New image available for download", { duration: 3000, icon: <ImageIcon sx={{ color: "grey" }} /> });
    }

    if (newModelAdded) {
      toast.success("New model available", { duration: 3000 });
    }

    setLastModelCount(urlSet.modelUrls.length);
    setLastPhotoCount(urlSet.photoUrls.length);
  }, [urlSet, setLastModelCount, setLastPhotoCount]);

  return (
    <AppBar
      position="sticky"
      sx={{
        backgroundColor: "#F5F5F5",
      }}
    >
      <Toolbar disableGutters>
        <Stack
          direction={"row"}
          spacing={2}
          paddingX={2}
          justifyContent={"space-between"}
          width={"100%"}
        >
          <Stack
            direction={"row"}
            spacing={2}
            alignItems={"center"}
            justifyContent={"flex-start"}
            flex={1}
          >
            <AttendeeSelect />
          </Stack>

          <Stack
            direction={"row"}
            spacing={1}
            alignItems={"center"}
            justifyContent={"center"}
            flex={1}
          >
            <ClearCallLogo size={30} />
            <ClearCallTitle size={20} />
          </Stack>

          <Stack
            direction={"row"}
            spacing={2}
            alignItems={"center"}
            justifyContent={"flex-end"}
            flex={1}
          >
            {connected && (
              <Stack
                direction={"row"}
                alignItems={"center"}
                justifyItems={"center"}
                spacing={1}
              >
                <ViewImagesButton
                  total={(urlSet?.photoUrls || []).length}
                  unseen={(urlSet?.photoUrls || []).length - seenImages.length}
                  onClick={openImageModal}
                />
                <ViewModelsButton
                  total={(urlSet?.modelUrls || []).length}
                  unseen={(urlSet?.modelUrls || []).length - seenModels.length}
                  onClick={openModelModal}
                />
                {children}
              </Stack>
            )}
            <Divider orientation="vertical" flexItem />
            <CallQuality showLabel />
          </Stack>

          {/* Image Viewer Modal */}
          <ImageViewerModal
            isOpen={imageModalOpen}
            onClose={closeImageModal}
            downloadURLs={urlSet?.photoUrls || []}
            patientId={urlSet?.patientDisplayId || ""}
            currentImageIndex={currentImageIndex}
            onImageIndexChange={selectCurrentImageIndex}
          />

          {/* Model Viewer Modal */}
          <ModelViewerModal
            isOpen={modelModalOpen}
            onClose={closeModelModal}
            imageSources={urlSet?.modelUrls || []}
            patientId={urlSet?.patientDisplayId || ""}
            currentImageIndex={currentModelIndex}
            onImageIndexChange={selectCurrentModelIndex}
          />
        </Stack>
      </Toolbar>
    </AppBar>
  );
};

export default MeetingToolbar;
